import React, { useState } from "react";
import { cn, Heading, IconBadge, Progress, gradient } from "@athena/ui";
import { ChevronLeft, Eye, FileText, Play, VideoIcon } from "lucide-react";
import { Avatar, AvatarGradientFallback, Button, Mono, Text } from "@athena/ui";
import { useTranslation } from "react-i18next";
import { PencilLine } from "lucide-react";
import { CourseVideoPlayer } from "./CourseVideoPlayer";
import type { Module } from "./CoursePreviewPage";

export type PreviewModule = Module;
type PreviewContentItem = PreviewModule["content"][number];
type VideoItem = Extract<PreviewContentItem, { __typename?: "VideoContent" }>;
type TextItem = Extract<PreviewContentItem, { __typename?: "TextContent" }>;
type QuizItem = Extract<PreviewContentItem, { __typename?: "QuizContent" }>;
type QuizQuestion = QuizItem["questions"][number];

// A flat step is either a non-quiz content item or a single quiz question
type VideoStep = { kind: "video"; item: VideoItem; moduleName: string };
type TextStep = { kind: "text"; item: TextItem; moduleName: string };
type QuizStep = {
    kind: "quiz";
    question: QuizQuestion;
    questionIndex: number;
    questionTotal: number;
    quizTitle: string;
    moduleName: string;
};
type Step = VideoStep | TextStep | QuizStep;

const contentSteps = (content: PreviewContentItem[], moduleName: string): Step[] =>
    content
        .filter((item) => item.status !== "HIDDEN")
        .flatMap((item): Step[] => {
            if (item.__typename === "VideoContent") {
                return [{ kind: "video", item, moduleName }];
            }
            if (item.__typename === "TextContent") {
                return [{ kind: "text", item, moduleName }];
            }
            if (item.__typename === "QuizContent") {
                const questions = [...item.questions].sort((a, b) => a.sortOrder - b.sortOrder);
                return questions.map((q, i) => ({
                    kind: "quiz",
                    question: q,
                    questionIndex: i,
                    questionTotal: questions.length,
                    quizTitle: item.title,
                    moduleName,
                }));
            }
            return [];
        });

// Ungrouped content (no moduleId) comes last, matching where the editor renders it below
// the modules list. It has no module name, so the step header just omits that suffix.
const buildSteps = (modules: PreviewModule[], unmoduledContent: PreviewContentItem[]): Step[] => [
    ...modules.flatMap((m) => contentSteps(m.content, m.title)),
    ...contentSteps(unmoduledContent, ""),
];

const stepTitle = (step: Step): string => {
    if (step.kind === "quiz") return step.quizTitle;
    return step.item.title;
};

const ReadingPreview = ({ title, body }: { title: string; body: string }) => (
    <div className="p-4">
        <IconBadge icon={<FileText className="h-3 w-3" />} variant="TEXT" className="my-2">
            Reading
        </IconBadge>
        <Heading level={6}>{title}</Heading>
        <Text size="sm" className="pt-4">
            {body}
        </Text>
    </div>
);

const VideoPreview = ({ step }: { step: VideoStep }) => (
    <div>
        <div className={cn("relative w-full shrink-0 bg-linear-to-br", gradient)} style={{ aspectRatio: "16/9" }}>
            {/* A processed asset isn't the only playable thing — an item can carry a
                legacy/external `videoUrl` with no `videoAssetId`, and CourseVideoPlayer
                already falls back to it. Gating on the asset alone dropped those to the
                placeholder art. The placeholder is still right when there's neither, i.e.
                an item whose video hasn't been attached yet. */}
            {step.item.videoAssetId || step.item.videoUrl ? (
                <CourseVideoPlayer
                    contentId={step.item.id}
                    videoUrl={step.item.videoUrl}
                    posterUrl={step.item.thumbnailUrl}
                    assetStatus={step.item.videoAsset?.status}
                />
            ) : (
                <>
                    <svg
                        className="absolute inset-0 h-full w-full opacity-[0.07]"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        {[
                            60,
                            100,
                            140,
                            180,
                            220,
                            260,
                            300,
                        ].map((r) => (
                            <circle key={r} cx="100%" cy="100%" r={r} fill="none" stroke="white" strokeWidth="1" />
                        ))}
                    </svg>
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.3)_100%)]" />
                    <div className="absolute inset-0 z-99 flex flex-col items-center justify-center gap-2">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full border-[0.5px] border-white/60 bg-white/20 backdrop-blur-sm">
                            <Play className="h-3 w-3 fill-white text-white" />
                        </div>
                        <Mono uppercase>Placeholder</Mono>
                    </div>
                </>
            )}
        </div>
        <div className="p-4">
            <IconBadge icon={<VideoIcon className="h-3 w-3" />} variant="VIDEO" className="my-2">
                Video
            </IconBadge>
            <Text className="font-semibold">{step.item.title}</Text>
        </div>
    </div>
);

const QuizQuestionPreview = ({ step }: { step: QuizStep }) => {
    const { t } = useTranslation();
    const options = step.question.options ?? [];

    return (
        <div className="p-4">
            <IconBadge icon={<FileText className="h-3 w-3" />} variant="QUIZ" className="my-2">
                Quiz · pass 70%
            </IconBadge>
            <Mono size="xs" className="text-muted-foreground mb-2">
                Question {step.questionIndex + 1} of {step.questionTotal}
            </Mono>
            <Heading level={6} className="mb-4">
                {step.question.questionText}
            </Heading>
            <div className="flex flex-col gap-2">
                {step.question.questionType === "SINGLE_SELECT" && (
                    <div className="flex flex-col gap-2">
                        <span className="text-muted-foreground text-xs">{t("admin.lms.clickCorrectAnswer")}</span>
                        <div className="flex flex-col gap-2">
                            {options.map((opt, i) => {
                                return (
                                    <div
                                        key={opt.id}
                                        className="border-border bg-background flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
                                    >
                                        <button
                                            type="button"
                                            aria-label={`Mark option ${i + 1} as correct`}
                                            className={cn(
                                                "focus-visible:ring-ring border-muted-foreground/30 hover:border-muted-foreground/50 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                                            )}
                                        ></button>
                                        <input
                                            value={opt.text}
                                            placeholder={`Option ${i + 1}`}
                                            className="text-foreground placeholder:text-muted-foreground flex-1 bg-transparent text-sm font-medium focus:outline-none"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {step.question.questionType === "MULTIPLE_SELECT" && (
                    <div className="flex flex-col gap-2">
                        <span className="text-muted-foreground text-xs">{t("admin.lms.clickCorrectAnswer")}</span>
                        <div className="flex flex-col gap-2">
                            {options.map((opt, i) => {
                                return (
                                    <div
                                        key={opt.id}
                                        className="border-border bg-background flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
                                    >
                                        <button
                                            type="button"
                                            aria-label={`Mark option ${i + 1} as correct`}
                                            className={cn(
                                                "focus-visible:ring-ring border-muted-foreground/30 hover:border-muted-foreground/50 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                                            )}
                                        ></button>
                                        <input
                                            value={opt.text}
                                            placeholder={`Option ${i + 1}`}
                                            className="text-foreground placeholder:text-muted-foreground flex-1 bg-transparent text-sm font-medium focus:outline-none"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {step.question.questionType === "SHORT_ANSWER" && (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-row items-center">
                            <IconBadge variant={"secondary"} icon={<PencilLine />}>
                                {t("admin.lms.openResponse")}
                            </IconBadge>
                        </div>

                        <div className="bg-muted/40 rounded-lg border">
                            <div className="flex flex-row justify-between p-4">
                                <Mono className="text-muted-foreground text-xs uppercase">
                                    Enter your response here
                                </Mono>
                            </div>
                        </div>
                    </div>
                )}
                {step.question.questionType === "LONG_ANSWER" && (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-row items-center">
                            <IconBadge variant={"secondary"} icon={<PencilLine />}>
                                {t("admin.lms.openResponse")}
                            </IconBadge>
                        </div>

                        <div className="bg-muted/40 h-50 rounded-lg border">
                            <div className="flex flex-row justify-between p-4">
                                <Mono className="text-muted-foreground text-xs uppercase">
                                    Enter your response here
                                </Mono>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const StepPreview = ({ step }: { step: Step }) => {
    if (step.kind === "video") return <VideoPreview step={step} />;
    if (step.kind === "text") return <ReadingPreview title={step.item.title} body={step.item.body} />;
    if (step.kind === "quiz") return <QuizQuestionPreview step={step} />;
    return null;
};

interface LearnerPreviewProps {
    modules: PreviewModule[];
    unmoduledContent: PreviewContentItem[];
    handlePreviewClicK: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LearnerPreview: React.FC<LearnerPreviewProps> = ({ modules, unmoduledContent, handlePreviewClicK }) => {
    const steps = buildSteps(modules, unmoduledContent);
    const totalSteps = steps.length;
    const [
        currentIndex,
        setCurrentIndex,
    ] = useState(0);

    const current = steps[currentIndex];
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === totalSteps - 1;

    return (
        <div className="bg-muted/30 flex flex-1 flex-col items-center overflow-y-auto p-8">
            <div className="flex flex-row items-center pb-6">
                <Eye className="mr-2 h-5 w-5" />
                <Text>Learner preview — this is exactly what someone enrolled will see.</Text>
            </div>
            <Button onClick={() => handlePreviewClicK((v) => !v)}>Back to edit</Button>

            <div className="border-accent-foreground bg-card mt-4 flex h-170 w-90 flex-col rounded-4xl border-10">
                <div className="h-22 shrink-0 rounded-t-4xl border-b p-4">
                    <div className="flex w-full flex-row items-center py-0.5">
                        <div className="w-8 shrink-0">
                            <Button variant="ghost" size="xs">
                                <ChevronLeft />
                            </Button>
                        </div>
                        <span className="flex-1 truncate text-center text-xs font-medium">
                            {current ? stepTitle(current) : "Course"}
                        </span>
                        <div className="w-8 shrink-0" />
                    </div>
                    <Progress
                        value={totalSteps > 0 ? Math.round(((currentIndex + 1) / totalSteps) * 100) : 0}
                        className="h-1"
                    />
                    <Mono size="xs" className="py-1">
                        Step {currentIndex + 1} of {totalSteps}
                        {current?.moduleName ? ` · ${current.moduleName}` : ""}
                    </Mono>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {totalSteps === 0 ? (
                        <div className="flex h-full items-center justify-center">
                            <div className="flex flex-col items-center gap-3 p-8 text-center">
                                <Avatar size="sm">
                                    <AvatarGradientFallback>?</AvatarGradientFallback>
                                </Avatar>
                                <Text size="sm" muted>
                                    No content yet. Add videos, readings, or quizzes to see the preview.
                                </Text>
                            </div>
                        </div>
                    ) : (
                        current && <StepPreview key={currentIndex} step={current} />
                    )}
                </div>

                {totalSteps > 0 && (
                    <div className="shrink-0 border-t p-4">
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-1/4"
                                disabled={isFirst}
                                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                            >
                                <ChevronLeft />
                            </Button>
                            <Button
                                size="sm"
                                className="w-3/4"
                                disabled={isLast}
                                onClick={() => setCurrentIndex((i) => Math.min(totalSteps - 1, i + 1))}
                            >
                                {isLast ? "Complete" : "Continue"}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {totalSteps > 0 && (
                <div className="mt-3 flex items-center justify-center gap-1.5">
                    {steps.map((_, i) => (
                        <div
                            key={i}
                            className={cn(
                                "h-1.5 rounded-full transition-all duration-300",
                                i === currentIndex ? "bg-primary w-5" : "bg-muted-foreground/30 w-1.5",
                            )}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
